import React, { useState, useEffect } from "react";
import * as Mui from "@mui/material";
import { useSelector, useDispatch } from "react-redux";
import { showToast } from "services/utils/Status";
import moment from "moment";
import { Loader } from "components/Loader";
import sessionHandling from "services/utils/notificationUtils";
import { BackArrow } from "components/CustomComponents/BackArrow";
import { DownloadForOffline } from "@mui/icons-material";
import { useFetch } from "services/hooks/useFetch";
import { themes, optionalAvathar } from "services/constants";
import { setGlobalSearchValue } from "services/Redux/userToken";
import BSDateRangePicker from "pages/MyZone/Status/DateRangePicker";
import { tooltipStyleProps } from "services/constants/Styles";
import { InfoMessage } from "components/CustomComponents/CustomButton";

const OvertimeReport = ({
  setShowReport,
  setOverTimeReportScreen,
  overTimeReportScreen,
}) => {
  const {
    domain,
    token,
    userDetails,
    globalSearchValue,
    topbarCompanyId,
    topbarCompanyName,
  } = useSelector((state) => state.tokenReducer);
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [otRepDetails, setOtRepDetails] = useState([]);
  const [masterDataForSearch, setMasterDataForSearch] = useState([]);
  const [startDate, setStartDate] = useState(
    moment().subtract(1, "day").format("DD/MM/YYYY")
  );
  const [endDate, setEndDate] = useState(
    moment().subtract(1, "day").format("DD/MM/YYYY")
  );
  const { result: departmentData } = useFetch(
    `${domain}get-department/?user_id=${userDetails.id}`
  );
  const [departmentName, setDepartmentName] = useState("All");
  const [departmentId, setDepartmentId] = useState("99999");

  useEffect(() => {
    const fetchUsertaskDetails = async () => {
      try {
        setIsLoading(true);
        let searchStartDate = startDate.split("/").reverse().join("-");
        let searchEndDate = endDate.split("/").reverse().join("-");

        const response = await fetch(
          `${domain}over-time-approve/?user_id=${userDetails.id}&department_id=${departmentId}&status=Approved&start_date=${searchStartDate}&end_date=${searchEndDate}&company_id=${topbarCompanyId}`,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `token ${token}`,
            },
          }
        );
        const res = await response.json();
        if (response.ok) {
          setOtRepDetails(res);
          setMasterDataForSearch(res);
        } else if (response.status === 409) {
          setOtRepDetails([]);
          setMasterDataForSearch([]);
          sessionHandling();
        } else {
          setOtRepDetails([]);
          setMasterDataForSearch([]);
          throw new Error(res.error);
        }
      } catch (error) {
        setOtRepDetails([]);
        setMasterDataForSearch([]);
        showToast("error", error.message);
      } finally {
        setIsLoading(false);
      }
    };
    fetchUsertaskDetails();
  }, [
    domain,
    token,
    startDate,
    endDate,
    departmentId,
    userDetails.id,
    topbarCompanyId,
  ]);

  useEffect(() => {
    const filteredResults = masterDataForSearch?.filter((item) =>
      [
        "date",
        "shift",
        "department_name",
        "username",
        "employee_id",
        "overtime_break_duration",
        "overtime_start",
        "overtime_end",
        "duration",
        "final_approved_duration",
      ].some((key) =>
        item[key]
          ?.toString()
          .toLowerCase()
          .includes(globalSearchValue.toString().toLowerCase())
      )
    );
    setOtRepDetails(filteredResults);
    setPage(0);
  }, [globalSearchValue, masterDataForSearch]);

  const sortedOtData = otRepDetails.sort((a, b) => {
    const [dayA, monthA, yearA] = a.date.split("-");
    const [dayB, monthB, yearB] = b.date.split("-");
    const formattedA = `${yearA}-${monthA}-${dayA}`;
    const formattedB = `${yearB}-${monthB}-${dayB}`;
    return formattedB.localeCompare(formattedA); // Descending
  });

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleStartDate = async (startDate, endDate) => {
    setStartDate(startDate);
    setEndDate(endDate);
  };

  const getOvertimeReport = () => {
    let dataPreparation = [];
    sortedOtData?.forEach((element) => {
      let overAllData = {
        date: element?.date,
        shiftname: element?.shift ? element?.shift : "Unassigned",
        departmentname: element?.department_name
          ? element?.department_name
          : "N/A",
        employeename: element?.username,
        employeeid: element?.employee_id,
        overtime_start: element?.overtime_start_formated
          ? element?.overtime_start_formated
          : "N/A",
        overtime_end: element?.overtime_end_formated
          ? element?.overtime_end_formated
          : "N/A",
        break_duration: element?.overtime_break_duration
          ? element?.overtime_break_duration
          : "00:00",
        duration: element?.overtime_duration
          ? element?.overtime_duration
          : "00:00",
        finalized_duration: element?.final_approved_duration
          ? element?.final_approved_duration
          : "00:00",
      };
      dataPreparation = [...dataPreparation, overAllData];
    });
    const OvertimeReport = ["Overtime Report"];
    const Space = [""];
    const Dates = ["Date"];
    const companyName = ["Company Name"];
    const Department = ["Department Name"];
    const GeneratedOn = ["Report Generated On"];
    OvertimeReport.push("");
    Dates.push(`${startDate} to ${endDate}`);
    companyName.push(topbarCompanyName);
    Department.push(departmentName);
    GeneratedOn.push(moment(new Date()).format("DD/MM/YYYY, hh:mm a"));
    Space.push("");
    const items = dataPreparation;
    const TableHead = ["Date"];
    TableHead.push("Shift Name");
    TableHead.push("Department Name");
    TableHead.push("Employee Name");
    TableHead.push("Employee Id");
    TableHead.push("Overtime Start");
    TableHead.push("Overtime End");
    TableHead.push("Break Duration");
    TableHead.push("Overtime Duration");
    TableHead.push("Final Duration");
    const replacer = (key, value) => (value === null ? "" : value);
    const header = Object.keys(items[0]);
    let csv = items.map((row) =>
      header
        .map((fieldName) => JSON.stringify(row[fieldName], replacer))
        .join(",")
    );
    csv.unshift(TableHead.join(","));
    csv.unshift(Space.join(","));
    csv.unshift(GeneratedOn.join(","));
    csv.unshift(Department.join(","));
    csv.unshift(companyName.join(","));
    csv.unshift(Dates.join(","));
    csv.unshift(OvertimeReport.join(","));
    csv = csv.join("\r\n");
    const ConvertData = csv;
    const element = document.createElement("a");
    const file = new Blob([ConvertData], {
      type: "text/plain",
    });
    element.href = URL.createObjectURL(file);
    element.download = "OvertimeReport.csv";
    document.body.appendChild(element);
    element.click();
  };

  return (
    <React.Fragment>
      <Mui.Grid container spacing={1} sx={{ mt: 0 }}>
        <Mui.Grid item xs={12} sm={12} md={12} lg={12}>
          <Mui.Stack direction="row">
            <BackArrow
              actionFunction={() => {
                dispatch(setGlobalSearchValue(""));
                setShowReport(false);
              }}
            />
            <Mui.Typography
              sx={{
                fontSize: 20,
                fontWeight: "bold",
                mt: 1.5,
              }}
            >
              Overtime Report
            </Mui.Typography>
          </Mui.Stack>
        </Mui.Grid>
      </Mui.Grid>

      <Mui.Grid container spacing={1} sx={{ mt: 1, paddingLeft: 2 }}>
        <Mui.Grid item xs={12} sm={6} md={3} lg={3}>
          <BSDateRangePicker
            handleStartDate={handleStartDate}
            showToday={false}
            triggerDate={true}
          />
        </Mui.Grid>
        <Mui.Grid item xs={12} sm={6} md={3} lg={2.5}>
          <Mui.Autocomplete
            disablePortal
            id="combo-box-demo"
            size="small"
            sx={{ minWidth: "100%" }}
            value={
              [{ id: 99999, department_name: "All" }, ...departmentData]?.find(
                (option) => option?.department_name === departmentName
              ) || {
                department_name: departmentName,
              }
            }
            options={[{ id: 99999, department_name: "All" }, ...departmentData]}
            ListboxProps={{
              style: {
                maxHeight: "150px",
              },
            }}
            filterOptions={(options, state) => {
              return options?.filter((option) =>
                option?.department_name
                  ?.toLowerCase()
                  .includes(state.inputValue.toLowerCase())
              );
            }}
            getOptionLabel={(option) =>
              option?.department_name ? option?.department_name : ""
            }
            isOptionEqualToValue={(option, value) =>
              option.department_name === value.department_name
            }
            renderOption={(props, option) => {
              return <li {...props}>{option?.department_name}</li>;
            }}
            onChange={(event, value) => {
              if (value && value?.department_name) {
                setDepartmentName(value.department_name);
                setDepartmentId(value.id);
              }
            }}
            renderInput={(params) => (
              <Mui.TextField
                {...params}
                size="small"
                label="Department"
                sx={{ background: "white" }}
                name="department name"
                placeholder="Select Department"
              />
            )}
          />
        </Mui.Grid>
        <Mui.Grid item lg={5.7}></Mui.Grid>
        <Mui.Grid item xs={2} sm={2} md={2} lg={0.5}>
          <Mui.IconButton
            onClick={() => {
              getOvertimeReport();
            }}
            disabled={sortedOtData?.length === 0 ? true : false}
          >
            <DownloadForOffline
              size="large"
              sx={{ color: themes.downloadIconColor, fontSize: 40, mt: -1 }}
            />
          </Mui.IconButton>
        </Mui.Grid>
      </Mui.Grid>

      {isLoading ? (
        <Loader info="Loading..." />
      ) : (
        <>
          <Mui.Grid container sx={{ overflow: "hidden" }}>
            <Mui.TableContainer
              sx={{ mt: 2, width: "100%", maxHeight: 660, ml: 2, mr: 2 }}
            >
              <Mui.Table className="border">
                <Mui.TableHead
                  sx={{
                    backgroundColor: themes.primary,
                    position: "sticky",
                    top: -1,
                    zIndex: 10,
                    boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
                  }}
                >
                  <Mui.TableRow
                    maxWidth="xl"
                    align="left"
                    sx={{
                      "& .MuiTableCell-root": {
                        height: "15px",
                        padding: "3px 15px",
                      },
                    }}
                  >
                    <Mui.TableCell
                      sx={{
                        color: themes.blackColor,
                        fontWeight: "bold",
                        overflow: "hidden",
                        whiteSpace: "nowrap",
                        maxWidth: 100,
                        width: "10%",
                        textOverflow: "ellipsis",
                      }}
                    >
                      Employee Name
                    </Mui.TableCell>
                    <Mui.TableCell
                      sx={{
                        color: themes.blackColor,
                        fontWeight: "bold",
                        overflow: "hidden",
                        whiteSpace: "nowrap",
                        maxWidth: 100,
                        width: "10%",
                        textOverflow: "ellipsis",
                      }}
                    >
                      Employee Id
                    </Mui.TableCell>
                    <Mui.TableCell
                      sx={{
                        color: themes.blackColor,
                        fontWeight: "bold",
                        overflow: "hidden",
                        whiteSpace: "nowrap",
                        maxWidth: 100,
                        width: "10%",
                        textOverflow: "ellipsis",
                      }}
                    >
                      Date
                    </Mui.TableCell>
                    <Mui.TableCell
                      sx={{
                        color: themes.blackColor,
                        fontWeight: "bold",
                        overflow: "hidden",
                        whiteSpace: "nowrap",
                        maxWidth: 100,
                        width: "10%",
                        textOverflow: "ellipsis",
                      }}
                    >
                      Shift Name
                    </Mui.TableCell>
                    <Mui.TableCell
                      sx={{
                        color: themes.blackColor,
                        fontWeight: "bold",
                        overflow: "hidden",
                        whiteSpace: "nowrap",
                        maxWidth: 100,
                        width: "10%",
                        textOverflow: "ellipsis",
                      }}
                    >
                      Department
                    </Mui.TableCell>

                    <Mui.TableCell
                      sx={{
                        color: themes.blackColor,
                        fontWeight: "bold",
                        overflow: "hidden",
                        whiteSpace: "nowrap",
                        maxWidth: 100,
                        width: "10%",
                        textOverflow: "ellipsis",
                      }}
                    >
                      OT Start
                    </Mui.TableCell>
                    <Mui.TableCell
                      sx={{
                        color: themes.blackColor,
                        fontWeight: "bold",
                        overflow: "hidden",
                        whiteSpace: "nowrap",
                        maxWidth: 100,
                        width: "10%",
                        textOverflow: "ellipsis",
                      }}
                    >
                      OT End
                    </Mui.TableCell>
                    <Mui.TableCell
                      sx={{
                        color: themes.blackColor,
                        fontWeight: "bold",
                        overflow: "hidden",
                        whiteSpace: "nowrap",
                        maxWidth: 100,
                        width: "10%",
                        textOverflow: "ellipsis",
                      }}
                    >
                      Break Duration
                    </Mui.TableCell>
                    <Mui.TableCell
                      sx={{
                        color: themes.blackColor,
                        fontWeight: "bold",
                        overflow: "hidden",
                        whiteSpace: "nowrap",
                        maxWidth: 100,
                        width: "10%",
                        textOverflow: "ellipsis",
                      }}
                    >
                      Duration
                    </Mui.TableCell>
                    <Mui.TableCell
                      sx={{
                        color: themes.blackColor,
                        fontWeight: "bold",
                        overflow: "hidden",
                        whiteSpace: "nowrap",
                        maxWidth: 100,
                        width: "10%",
                        textOverflow: "ellipsis",
                      }}
                    >
                      Approved Duration
                    </Mui.TableCell>
                  </Mui.TableRow>
                </Mui.TableHead>
                <Mui.TableBody>
                  {sortedOtData?.length === 0 ? (
                    <InfoMessage dataLength={masterDataForSearch} />
                  ) : (
                    sortedOtData
                      ?.slice(
                        page * rowsPerPage,
                        page * rowsPerPage + rowsPerPage
                      )
                      ?.map((item, index) => (
                        <Mui.TableRow
                          key={item?.id}
                          sx={{
                            "& .MuiTableCell-root": {
                              height: "10px",
                              padding: "3px 15px",
                            },
                          }}
                        >
                          <Mui.TableCell
                            sx={{
                              overflow: "hidden",
                              whiteSpace: "nowrap",
                              maxWidth: 100,
                            }}
                          >
                            <Mui.Tooltip
                              arrow
                              slotProps={tooltipStyleProps}
                              title={item?.username}
                              disableHoverListener={!item?.username}
                            >
                              <Mui.Stack direction="Row">
                                <Mui.Avatar
                                  sx={{ width: 23, height: 23 }}
                                  alt="Remy Sharp"
                                  src={item?.image_url ?? optionalAvathar}
                                />
                                <Mui.Typography
                                  sx={{
                                    overflow: "hidden",
                                    whiteSpace: "nowrap",
                                    textOverflow: "ellipsis",
                                    paddingLeft: 1,
                                  }}
                                >
                                  {item?.user_name}
                                </Mui.Typography>
                              </Mui.Stack>
                            </Mui.Tooltip>
                          </Mui.TableCell>
                          <Mui.TableCell
                            sx={{
                              overflow: "hidden",
                              whiteSpace: "nowrap",
                              maxWidth: 100,
                            }}
                          >
                            <Mui.Typography
                              sx={{
                                overflow: "hidden",
                                whiteSpace: "nowrap",
                                textOverflow: "ellipsis",
                              }}
                            >
                              {item?.employee_id}
                            </Mui.Typography>
                          </Mui.TableCell>
                          <Mui.TableCell
                            sx={{
                              overflow: "hidden",
                              whiteSpace: "nowrap",
                              maxWidth: 100,
                            }}
                          >
                            <Mui.Typography
                              sx={{
                                overflow: "hidden",
                                whiteSpace: "nowrap",
                                textOverflow: "ellipsis",
                              }}
                            >
                              {item?.date}
                            </Mui.Typography>
                          </Mui.TableCell>
                          <Mui.TableCell
                            sx={{
                              overflow: "hidden",
                              whiteSpace: "nowrap",
                              maxWidth: 100,
                            }}
                          >
                            <Mui.Tooltip
                              arrow
                              slotProps={tooltipStyleProps}
                              title={item?.shift_name}
                              disableHoverListener={!item?.shift_name}
                            >
                              <Mui.Typography
                                sx={{
                                  overflow: "hidden",
                                  whiteSpace: "nowrap",
                                  textOverflow: "ellipsis",
                                }}
                              >
                                {item?.shift ? item?.shift : "Unassigned"}
                              </Mui.Typography>
                            </Mui.Tooltip>
                          </Mui.TableCell>
                          <Mui.TableCell
                            sx={{
                              overflow: "hidden",
                              whiteSpace: "nowrap",
                              maxWidth: 100,
                            }}
                          >
                            <Mui.Tooltip
                              arrow
                              slotProps={tooltipStyleProps}
                              title={item?.department_name}
                              disableHoverListener={!item?.department_name}
                            >
                              <Mui.Typography
                                sx={{
                                  overflow: "hidden",
                                  whiteSpace: "nowrap",
                                  textOverflow: "ellipsis",
                                }}
                              >
                                {item?.department_name}
                              </Mui.Typography>
                            </Mui.Tooltip>
                          </Mui.TableCell>

                          <Mui.TableCell
                            sx={{
                              overflow: "hidden",
                              whiteSpace: "nowrap",
                              maxWidth: 100,
                            }}
                          >
                            <Mui.Typography
                              sx={{
                                overflow: "hidden",
                                whiteSpace: "nowrap",
                                textOverflow: "ellipsis",
                              }}
                            >
                              {item?.overtime_start_formated
                                ? item?.overtime_start_formated
                                : "N/A"}
                            </Mui.Typography>
                          </Mui.TableCell>
                          <Mui.TableCell
                            sx={{
                              overflow: "hidden",
                              whiteSpace: "nowrap",
                              maxWidth: 100,
                            }}
                          >
                            <Mui.Typography
                              sx={{
                                overflow: "hidden",
                                whiteSpace: "nowrap",
                                textOverflow: "ellipsis",
                              }}
                            >
                              {item?.overtime_end_formated
                                ? item?.overtime_end_formated
                                : "N/A"}
                            </Mui.Typography>
                          </Mui.TableCell>
                          <Mui.TableCell
                            sx={{
                              overflow: "hidden",
                              whiteSpace: "nowrap",
                              maxWidth: 100,
                            }}
                          >
                            <Mui.Typography
                              sx={{
                                overflow: "hidden",
                                whiteSpace: "nowrap",
                                textOverflow: "ellipsis",
                              }}
                            >
                              {item?.overtime_break_duration
                                ? item?.overtime_break_duration
                                : "00:00"}
                            </Mui.Typography>
                          </Mui.TableCell>
                          <Mui.TableCell
                            sx={{
                              overflow: "hidden",
                              whiteSpace: "nowrap",
                              maxWidth: 100,
                            }}
                          >
                            <Mui.Typography
                              sx={{
                                overflow: "hidden",
                                whiteSpace: "nowrap",
                                textOverflow: "ellipsis",
                              }}
                            >
                              {item?.overtime_duration
                                ? item?.overtime_duration
                                : "00:00"}
                            </Mui.Typography>
                          </Mui.TableCell>
                          <Mui.TableCell
                            sx={{
                              overflow: "hidden",
                              whiteSpace: "nowrap",
                              maxWidth: 100,
                            }}
                          >
                            <Mui.Typography
                              sx={{
                                overflow: "hidden",
                                whiteSpace: "nowrap",
                                textOverflow: "ellipsis",
                              }}
                            >
                              {item?.final_approved_duration
                                ? item?.final_approved_duration
                                : "00:00"}
                            </Mui.Typography>
                          </Mui.TableCell>
                        </Mui.TableRow>
                      ))
                  )}
                </Mui.TableBody>
              </Mui.Table>
            </Mui.TableContainer>
          </Mui.Grid>

          {sortedOtData?.length > 10 && (
            <Mui.TablePagination
              className="custom-pagination"
              component="div"
              rowsPerPageOptions={[25, 50, 75, 100]}
              count={sortedOtData?.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          )}
        </>
      )}
    </React.Fragment>
  );
};

export default OvertimeReport;
